import { Box, Button, Calendar, DatePicker, Modal, TextBlock, TextInput } from "@bcc-ui/ui-components";
import classNames from "classnames";
import * as React from "react";
import { useTranslation } from "react-i18next";
import QRCode from "react-qr-code";

import CopyToClipboard from "components/copy-to-clipboard/CopyToClipboard";
import style from "components/reports/passports/passports-view.scss";
import { getLanguage } from "services/language/languageRepository";
import { passportService } from "services/report/passports/PassportService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { DATE_FORMAT } from "utils/format";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

// The backend rejects expirations more than 5 years from now. It expires links at the end of the chosen date in UTC.
const MAX_EXPIRATION_YEARS = 5;

// The last selectable day is the one before the 5-year mark.
const getLatestExpiration = (): Date => {
    const latest = new Date();
    latest.setFullYear(latest.getFullYear() + MAX_EXPIRATION_YEARS);
    latest.setDate(latest.getDate() - 1);
    return latest;
};

const startOfDay = (date: Date): number => new Date(date).setHours(0, 0, 0, 0);

// Today is not selectable: whether it is already over depends on the time zone, and links end at the end of the day
// in UTC.
const getEarliestExpiration = (): Date => {
    const earliest = new Date();
    earliest.setDate(earliest.getDate() + 1);
    return earliest;
};

const isExpirationAllowed = (date: Date): boolean =>
    startOfDay(date) >= startOfDay(getEarliestExpiration()) && startOfDay(date) <= startOfDay(getLatestExpiration());

const buildShareUrl = (token: string): string =>
    `${window.location.origin}/passports/shared?token=${encodeURIComponent(token)}`;

interface Props {
    isOpen: boolean;
    searchValue: string;
    shareUrl?: string;
    onLinkCreated: (shareUrl: string) => void;
    onClose: () => void;
}

// Render the calendar inside the modal, which is fixed positioned, so that it doesn't open behind it.
const getDatePickerContainer = (anchor: HTMLElement | null): HTMLElement => {
    let element = anchor;
    while (element) {
        if (getComputedStyle(element).position === "fixed") {
            return element;
        }
        element = element.parentElement;
    }
    return document.body;
};

export const PassportShareDialog = (props: Props) => {
    const { t } = useTranslation();
    const { shareUrl } = props;
    const theme = useTheme();
    const datePickerAnchorRef = React.useRef<HTMLDivElement>(null);
    const [expiration, setExpiration] = React.useState<Date | null>(null);
    const [creating, setCreating] = React.useState(false);
    const [failed, setFailed] = React.useState(false);

    const createLink = () => {
        if (!expiration) {
            return;
        }
        usageStatisticsService.sendEvent({
            category: Category.DEVICE_PASSPORT,
            action: Action.CREATE_DEVICE_PASSPORT_LINK,
        });
        setCreating(true);
        setFailed(false);
        passportService
            .createLink(props.searchValue, expiration, getLanguage().code)
            .then((token) => {
                props.onLinkCreated(buildShareUrl(token));
            })
            .catch(() => setFailed(true))
            .finally(() => setCreating(false));
    };

    const buttons = shareUrl ? (
        <Button variant="Primary" onClick={props.onClose} testId={testIds.common.dialog.closeButton}>
            {t("Common.ok")}
        </Button>
    ) : (
        <Button
            variant="Primary"
            onClick={createLink}
            isLoading={creating}
            disabled={creating || !expiration || !isExpirationAllowed(expiration)}
            testId={testIds.workArea.passports.shareDialog.createButton}
        >
            {t("Common.create")}
        </Button>
    );

    return (
        <Modal
            isOpen={props.isOpen}
            hideModal={props.onClose}
            title={t("DevicePassport.share.title")}
            buttons={buttons}
            testId={testIds.workArea.passports.shareDialog.itself}
        >
            <Box columnLayout gap="Sm">
                {shareUrl ? (
                    <>
                        <Box columnLayout gap="Xxxs">
                            <Box gap="Xxs" verticalAlignment="Center">
                                <TextInput
                                    name="passport-share-link"
                                    hideLabelSection
                                    readOnly
                                    aria-label={t("DevicePassport.share.link")}
                                    value={shareUrl}
                                    className={style.fullWidthInputWrapper}
                                    testIds={{ input: testIds.workArea.passports.shareDialog.linkInput }}
                                />
                                <Box className={style.copyLink}>
                                    <CopyToClipboard
                                        value={shareUrl}
                                        isNewTable
                                        testId={testIds.workArea.passports.shareDialog.copyButton}
                                    />
                                </Box>
                            </Box>
                        </Box>
                        <Box horizontalAlignment="Center">
                            <div className={style.qrCode} data-testid={testIds.workArea.passports.shareDialog.qrCode}>
                                <QRCode value={shareUrl} size={200} />
                            </div>
                        </Box>
                    </>
                ) : (
                    <Box columnLayout gap="Xxxs">
                        <TextBlock disableBottomSpacing className={style.fieldLabel}>
                            {t("Common.expirationDate")}
                        </TextBlock>
                        <div ref={datePickerAnchorRef}>
                            <DatePicker
                                name="passport-share-expiration"
                                className={classNames(style.fullWidthInputWrapper, style.expirationPicker)}
                                hideLabelSection
                                aria-label={t("Common.expirationDate")}
                                caretAs={() => <Calendar color={theme.iconFillColor} />}
                                container={() => getDatePickerContainer(datePickerAnchorRef.current)}
                                placement="bottomStart"
                                format={DATE_FORMAT}
                                placeholder={t("DevicePassport.share.expirationPlaceholder")}
                                value={expiration}
                                onChange={setExpiration}
                                cleanable={false}
                                disabled={creating}
                                shouldDisableDate={(date) => !isExpirationAllowed(date)}
                                error={failed ? t("DevicePassport.share.failed") : undefined}
                                testId={testIds.workArea.passports.shareDialog.expirationInput}
                            />
                        </div>
                    </Box>
                )}
            </Box>
        </Modal>
    );
};
